import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import usePresence from '../use/usePresence';
import {
  X, CaretRight, CaretLeft, Check, Icon, IconWeight,
  Sparkle, ChartBar, ClipboardText, BookOpen, ListChecks, TrendUp, Camera, Gear, RocketLaunch,
} from 'phosphor-react';

interface OnboardingTourProps {
  /**
   * Quem decide se o tour aparece. Antes o App montava/desmontava com
   * `{tourAqui && <OnboardingTour/>}` — e desmontar é instantâneo, então o
   * cartão tinha entrada e sumia no frame. Agora a prop entra e a saída existe.
   */
  aberto: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
}

interface StepData {
  title: string;
  description: string;
  Icon: Icon;
  iconWeight?: IconWeight;
  route: string;
}

export default function OnboardingTour({ aberto, onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const { montado, estado } = usePresence(aberto, 200);

  const steps: StepData[] = [
    {
      title: 'Bem-vindo ao AutoCare!',
      description: 'Vamos fazer um tour rápido pelas funcionalidades do app. Acompanhe cada página ao fundo!',
      Icon: Sparkle,
      route: '/dashboard',
    },
    {
      title: 'Dashboard',
      description: 'Seu painel principal: aqui você monta e edita a rotina da manhã e da noite, e vê o progresso recente.',
      Icon: ChartBar,
      iconWeight: 'fill',
      route: '/dashboard',
    },
    {
      title: 'Quiz de Pele',
      description: 'Responda um questionário completo sobre seu tipo de pele, hábitos e objetivos para receber uma rotina personalizada.',
      Icon: ClipboardText,
      route: '/quiz',
    },
    {
      title: 'Biblioteca de Produtos',
      description: 'Explore produtos de skincare e adicione os seus favoritos à sua rotina.',
      Icon: BookOpen,
      route: '/products',
    },
    {
      title: 'Checklist Diário',
      description: 'Marque os passos que você completou no dia e mantenha a consistência na sua rotina.',
      Icon: ListChecks,
      route: '/routine-checklist',
    },
    {
      title: 'Progresso',
      description: 'Registre logs diários e acompanhe a evolução da sua pele ao longo do tempo.',
      Icon: TrendUp,
      route: '/progress',
    },
    {
      title: 'Fotos da Pele',
      description: 'Tire fotos da sua pele e compare a evolução lado a lado com o passar dos dias.',
      Icon: Camera,
      iconWeight: 'fill',
      route: '/skin-photos',
    },
    {
      title: 'Configurações',
      description: 'Gerencie seu perfil, tema, refaça a anamnese ou reporte bugs.',
      Icon: Gear,
      iconWeight: 'fill',
      route: '/settings',
    },
    {
      title: 'Tudo pronto!',
      description: 'Comece fazendo o quiz de pele para criar sua rotina personalizada. Bons cuidados!',
      Icon: RocketLaunch,
      iconWeight: 'fill',
      route: '/quiz',
    },
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
  };

  useEffect(() => {
    if (aberto && currentStepData.route) {
      navigate(currentStepData.route);
    }
  }, [aberto, currentStep, currentStepData.route, navigate]);

  const StepIcon = currentStepData.Icon;

  if (!montado) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 pointer-events-none data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
    >
      <div className="fixed bottom-6 right-6 w-[360px] max-w-[calc(100vw-3rem)] pointer-events-auto data-[state=closed]:pointer-events-none">
        {/* Entra pela lateral (500ms, é visto uma vez); sai pelo `exit` padrão —
            criar um `slide-out` só para isto seria dialeto novo à toa. */}
        <div
          data-state={estado}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden data-[state=open]:animate-slide-in data-[state=closed]:animate-exit"
        >
          <div className="bg-gradient-to-r from-blush-400 to-blush-500 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <StepIcon size={24} weight={currentStepData.iconWeight} className="text-white" />
                <span className="text-white font-semibold text-sm">
                  Passo {currentStep + 1}/{steps.length}
                </span>
              </div>
              <button
                onClick={handleSkip}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Fechar tour"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-colors duration-300 ease-soft ${
                    index <= currentStep ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              {currentStepData.description}
            </p>

            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={handlePrevious}
                  className="flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors active:scale-[0.97]"
                >
                  <CaretLeft className="w-4 h-4" />
                  Voltar
                </button>
              )}

              {isLastStep ? (
                <button
                  onClick={handleComplete}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blush-400 to-blush-500 text-white text-sm font-medium hover:shadow-lg transition-shadow active:scale-[0.97]"
                >
                  <Check className="w-4 h-4" />
                  Começar
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blush-400 to-blush-500 text-white text-sm font-medium hover:shadow-lg transition-shadow active:scale-[0.97]"
                >
                  Próximo
                  <CaretRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {!isLastStep && (
              <button
                onClick={handleSkip}
                className="w-full mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Pular tour
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
